import { Utils } from '@sbs/uikit-constructor';
import { FileTypes, getFileType } from '@/utils/fileTypes.js';
import { getImageFileFromUrl } from '@/utils/getImageFileFromUrl.js';
import { mimePasteToExtMap } from '@/admin/components/AI/constants.js';
import { getDOMHtml } from '@/utils/utils.js';

/**
 * @typedef {Object} FileData
 * @property {string} id
 * @property {string} name
 * @property {number} size
 * @property {string} type
 * @property {number} [progress]
 * @property {string} [title]
 * @property {File} [originalFile]
 * @property {string} [fileIcon]
 * @property {string} [fileIconTheme]
 * @property {string} [fileBackgroundTheme]
 * @property {string} [fileHoverBackground]
 * @property {string} [loaderStrokeTheme]
 */

/**
 * @typedef {Object} FileAttachment
 * @property {string} file_name
 * @property {string} file_size
 * @property {number} file_mime
 * @property {number} [progress]
 */

/**
 * @param {File|FileAttachment} file
 * @param {Object} [options]
 * @param {boolean} options.downloadable
 * @returns {FileData|{}}
 */
// eslint-disable-next-line complexity
export const getFileData = (file, options = {}) => {
    if (!file) return {};

    const { downloadable } = options;

    const ext = Utils.FileTypes.getFileExtension((file?.name || file?.file_name) ?? '');
    const type = getFileType(ext);
    const icon = FileTypes[type].ICON;
    const iconColor = FileTypes[type].ICON_COLOR;
    const strokeColor = FileTypes[type].STROKE_COLOR;
    const backgroundColor = FileTypes[type].BACKGROUND_COLOR;
    const hoverBackground = downloadable
        ? 'white'
        : 'gray-ultra-light';

    return {
        ...file,
        id: file.id || Utils.Helpers.getRandomId(),
        fileIcon: icon,
        fileIconTheme: iconColor,
        fileBackgroundTheme: backgroundColor,
        fileHoverBackground: hoverBackground,
        loaderStrokeTheme: strokeColor,
        name: file.name || file.file_name,
        progress: file.progress ?? 100,
        size: file.size || file.file_size,
        type: file.type || file.mime || file.file_mime,
        originalFile: file instanceof File
            ? file
            : file.originalFile,
        title: file instanceof File
            ? Utils.FileTypes.getFileName(file.name || file.file_name)
            : file.title,
    };
};


/**
 * @param {Blob|string} file
 * @return {Promise<{width, height}>}
 */
export const getImageSize = file => new Promise((resolve, reject) => {
    try {
        const isFileString = typeof file === 'string';
        const imgUrl = isFileString ? file : URL.createObjectURL(file);
        const img = new Image();

        const clearResources = () => {
            if (!isFileString && imgUrl) {
                URL.revokeObjectURL(imgUrl);
            }
        };

        img.addEventListener('load', () => {
            resolve({
                width: img.naturalWidth,
                height: img.naturalHeight,
            });
            clearResources();
        });

        img.addEventListener('error', e => {
            reject(e);
            clearResources();
        });

        img.src = imgUrl;
    } catch (e) {
        reject(e);
    }
});

/**
 * @param {HTMLImageElement} img
 * @param {object} [options]
 * @param {string} [options.filename]
 * @returns {Promise<File>}
 */
export async function getImgToFile(img, {
    filename = 'image.png',
    withFallback = true,
} = {}) {
    try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        ctx.drawImage(img, 0, 0);

        return await new Promise((resolve, reject) => {
            canvas.toBlob(blob => {
                if (blob) {
                    resolve(new File([blob], filename, { type: blob.type }));
                } else {
                    reject(new Error('Canvas toBlob failed'));
                }
            }, 'image/png');
        });
    } catch (err) {
        console.warn('Canvas method failed, fallback to fetch:', err);

        if (withFallback) {
            return getImageFileFromUrl(img.src, filename);
        }

        throw err;
    }
}

export const getImageFromHtml = async html => {
    const htmlDom = getDOMHtml(html);

    const img = htmlDom.querySelector('img');

    if (!img) return null;

    const file = await getImageFileFromUrl(img.src);

    return file;
};

export const getImageFromClipBoard = async () => {
    const [item] = await navigator.clipboard.read();

    for (const type of item.types) {
        const fileImageExt = mimePasteToExtMap[type];

        if (fileImageExt) {
            // eslint-disable-next-line no-await-in-loop
            const blob = await item.getType(type);

            return new File([blob], `${Utils.Helpers.getRandomId()}.${fileImageExt}`, { type });
        }
    }

    if (item.types.includes('text/html')) {
        const htmlBlob = await item.getType('text/html');
        const html = await htmlBlob.text();

        return getImageFromHtml(html);
    }

    return null;
};
